import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Checkbox,
  Grid,
  Stack,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  MonitorHeart as MonitorIcon,
  Checklist as ChecklistIcon,
  Download as DownloadIcon,
  FolderZip as ZipIcon,
  Description as DocIcon,
  Dashboard as DashboardIcon,
  NotificationsActive as AlertIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { OperationsOutput, CodegenOutput } from '../../../services/workflow';

interface OperationsSpecEditorProps {
  operations: OperationsOutput | null;
  codegen: CodegenOutput | null;
  qualityScore?: number;
  qualityScoreLabel?: string;
  isLoading?: boolean;
}

const OperationsSpecEditor: React.FC<OperationsSpecEditorProps> = ({
  operations,
  codegen,
  qualityScore,
  qualityScoreLabel = 'Completeness',
  isLoading = false,
}) => {
  const [tab, setTab] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleCheckItem = (category: string, item: string) => {
    const key = `${category}:${item}`;
    setCheckedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleDownloadZip = useCallback(async () => {
    if (!codegen?.files) {
      setSnackbarMessage('No code files available to download');
      setSnackbarOpen(true);
      return;
    }

    setIsDownloading(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Add code files
      const codeFolder = zip.folder(codegen.project_name || 'agent_project');
      if (codeFolder) {
        Object.entries(codegen.files).forEach(([name, content]) => {
          codeFolder.file(name, content);
        });
      }

      // Add operations spec as markdown
      if (operations?.document) {
        const docsFolder = zip.folder('docs');
        if (docsFolder) {
          docsFolder.file('operations_spec.md', operations.document);
        }
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${codegen.project_name || 'agent_project'}_complete.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSnackbarMessage('Complete project ZIP downloaded!');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to create ZIP:', err);
      setSnackbarMessage('Failed to create ZIP file');
      setSnackbarOpen(true);
    } finally {
      setIsDownloading(false);
    }
  }, [codegen, operations]);

  // Empty state
  if (!operations) {
    return (
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <MoneyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          The Operations Expert will provide cost estimates, monitoring setup,
          and deployment guidance.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Operations planning will appear here shortly.
        </Typography>
      </Paper>
    );
  }

  const costData = operations.structured_data?.cost_estimate;
  const monitoringData = operations.structured_data?.monitoring_config;
  const checklistData = operations.structured_data?.deployment_checklist;

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header with quality score */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Operations Plan
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={
                operations.deployment_platform
                  ?.replace('_', ' ')
                  .toUpperCase() || 'Cloud Run'
              }
              size="small"
              color="primary"
              variant="outlined"
            />
            {qualityScore !== undefined && (
              <Chip
                label={`${qualityScoreLabel}: ${Math.round(qualityScore * 100)}%`}
                size="small"
                color={
                  qualityScore >= 0.8
                    ? 'success'
                    : qualityScore >= 0.5
                      ? 'warning'
                      : 'default'
                }
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        {qualityScore !== undefined && (
          <LinearProgress
            variant="determinate"
            value={qualityScore * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              mb: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  qualityScore >= 0.8
                    ? 'success.main'
                    : qualityScore >= 0.5
                      ? 'warning.main'
                      : 'grey.400',
              },
            }}
          />
        )}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="operations spec tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<DocIcon fontSize="small" />}
            iconPosition="start"
            label="Overview"
          />
          <Tab
            icon={<MoneyIcon fontSize="small" />}
            iconPosition="start"
            label="Costs"
          />
          <Tab
            icon={<MonitorIcon fontSize="small" />}
            iconPosition="start"
            label="Monitoring"
          />
          <Tab
            icon={<ChecklistIcon fontSize="small" />}
            iconPosition="start"
            label="Checklist"
          />
          <Tab
            icon={<DownloadIcon fontSize="small" />}
            iconPosition="start"
            label="Export"
          />
        </Tabs>
      </Box>

      {/* Loading indicator */}
      {isLoading && <LinearProgress />}

      {/* Content area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {/* Overview Tab - Markdown Document */}
        {tab === 0 && (
          <Box
            sx={{
              '& h1': { fontSize: '1.5rem', fontWeight: 'bold', mt: 2, mb: 1 },
              '& h2': { fontSize: '1.25rem', fontWeight: 'bold', mt: 2, mb: 1 },
              '& h3': {
                fontSize: '1.1rem',
                fontWeight: 'bold',
                mt: 1.5,
                mb: 0.5,
              },
              '& p': { mb: 1 },
              '& ul, & ol': { pl: 2, mb: 1 },
              '& li': { mb: 0.5 },
              '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
                textAlign: 'left',
              },
              '& th': { bgcolor: 'grey.100' },
              '& blockquote': {
                borderLeft: 3,
                borderColor: 'primary.main',
                pl: 2,
                ml: 0,
                color: 'text.secondary',
                fontStyle: 'italic',
              },
              '& hr': { my: 2, borderColor: 'divider' },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {operations.document ||
                'No operations specification generated yet.'}
            </ReactMarkdown>
          </Box>
        )}

        {/* Costs Tab */}
        {tab === 1 && costData && (
          <Box>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <MoneyIcon color="primary" />
                  <Typography variant="h6">Monthly Cost Estimate</Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Input Tokens
                    </Typography>
                    <Typography variant="h6">
                      {costData.monthly_estimate?.input_tokens?.toLocaleString() ||
                        0}
                    </Typography>
                    <Typography variant="body1" color="primary.main">
                      $
                      {costData.monthly_estimate?.input_cost_usd?.toFixed(2) ||
                        '0.00'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      Output Tokens
                    </Typography>
                    <Typography variant="h6">
                      {costData.monthly_estimate?.output_tokens?.toLocaleString() ||
                        0}
                    </Typography>
                    <Typography variant="body1" color="primary.main">
                      $
                      {costData.monthly_estimate?.output_cost_usd?.toFixed(2) ||
                        '0.00'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Total Estimated
                  </Typography>
                  <Typography
                    variant="h5"
                    color="success.main"
                    fontWeight="bold"
                  >
                    $
                    {costData.monthly_estimate?.total_cost_usd?.toFixed(2) ||
                      '0.00'}
                    /mo
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Model: {costData.model || 'gemini-2.0-flash'}
                </Typography>
              </CardContent>
            </Card>

            {costData.optimization_tips &&
              costData.optimization_tips.length > 0 && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Optimization Tips
                    </Typography>
                    <List dense>
                      {costData.optimization_tips.map((tip, idx) => (
                        <ListItem key={idx} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText primary={tip} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}

            {costData.disclaimer && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: 'block' }}
              >
                {costData.disclaimer}
              </Typography>
            )}
          </Box>
        )}

        {/* Monitoring Tab */}
        {tab === 2 && monitoringData && (
          <Box>
            {/* Metrics */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <MonitorIcon color="primary" />
                  <Typography variant="h6">Metrics to Track</Typography>
                </Box>
                <List dense>
                  {monitoringData.monitoring?.metrics?.map((metric, idx) => (
                    <ListItem key={idx}>
                      <ListItemText
                        primary={metric.name.replace(/_/g, ' ').toUpperCase()}
                        secondary={`${metric.description} (${metric.unit})${metric.alert_threshold ? ` - Alert threshold: ${metric.alert_threshold}` : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Dashboards */}
            {monitoringData.monitoring?.dashboards && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <DashboardIcon color="primary" />
                    <Typography variant="h6">Recommended Dashboards</Typography>
                  </Box>
                  <List dense>
                    {monitoringData.monitoring.dashboards.map(
                      (dashboard, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <DashboardIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={dashboard} />
                        </ListItem>
                      )
                    )}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Alerting */}
            {monitoringData.alerting && (
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <AlertIcon color="warning" />
                    <Typography variant="h6">Alert Conditions</Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Alerts will be sent to: {monitoringData.alerting.email}
                  </Typography>
                  <List dense>
                    {monitoringData.alerting.conditions?.map(
                      (condition, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <AlertIcon fontSize="small" color="warning" />
                          </ListItemIcon>
                          <ListItemText primary={condition} />
                        </ListItem>
                      )
                    )}
                  </List>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* Checklist Tab */}
        {tab === 3 && checklistData && (
          <Box>
            {Object.entries(checklistData.checklist || {}).map(
              ([category, items]) => (
                <Card key={category} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {category.replace(/_/g, ' ')}
                    </Typography>
                    <List dense>
                      {(
                        items as Array<{ item: string; required: boolean }>
                      ).map((item, idx) => {
                        const isChecked = checkedItems.has(
                          `${category}:${item.item}`
                        );
                        return (
                          <ListItem
                            key={idx}
                            sx={{ py: 0.5, cursor: 'pointer' }}
                            onClick={() => handleCheckItem(category, item.item)}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Checkbox
                                edge="start"
                                checked={isChecked}
                                icon={<UncheckedIcon />}
                                checkedIcon={<CheckIcon color="success" />}
                                sx={{ p: 0 }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <span
                                  style={{
                                    textDecoration: isChecked
                                      ? 'line-through'
                                      : 'none',
                                  }}
                                >
                                  {item.item}
                                  {item.required && (
                                    <Chip
                                      label="required"
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ ml: 1, height: 20 }}
                                    />
                                  )}
                                </span>
                              }
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </CardContent>
                </Card>
              )
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Target Platform:{' '}
              <strong>
                {checklistData.deployment_target
                  ?.replace('_', ' ')
                  .toUpperCase()}
              </strong>
            </Typography>
          </Box>
        )}

        {/* Export Tab */}
        {tab === 4 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Export & Deploy
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Download your complete project including generated code and
                  operations documentation.
                </Typography>

                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={
                      isDownloading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <ZipIcon />
                      )
                    }
                    fullWidth
                    sx={{ justifyContent: 'flex-start', py: 1.5 }}
                    onClick={handleDownloadZip}
                    disabled={isDownloading || !codegen?.files}
                  >
                    Download Complete Project (ZIP)
                  </Button>

                  <Divider>
                    <Typography variant="caption" color="text.secondary">
                      Coming Soon
                    </Typography>
                  </Divider>

                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    fullWidth
                    sx={{ justifyContent: 'flex-start', py: 1.5 }}
                    disabled
                  >
                    Push to GitHub Repository
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    fullWidth
                    sx={{ justifyContent: 'flex-start', py: 1.5 }}
                    disabled
                  >
                    Deploy to Google Cloud Run
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {codegen && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Package Contents
                </Typography>
                <Typography variant="body2">
                  <strong>Project:</strong> {codegen.project_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Files:</strong> {Object.keys(codegen.files).length}{' '}
                  code files + operations docs
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong>{' '}
                  {codegen.is_multi_agent
                    ? 'Multi-Agent System'
                    : 'Single Agent'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default OperationsSpecEditor;
